import {
    AccountCircle,
    Assessment,
    Chat,
    ChevronLeft,
    ChevronRight,
    Dashboard,
    Description,
    ExpandMore,
    Gavel,
    Logout,
    Menu,
    ViewWeek,
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Collapse,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ThemeProvider,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import AccountSwitcher from './AccountSwitcher';
import ApplicationSelector from './ApplicationSelector';
import ProfileAvatar from './ProfileAvatar';
import { useWorkspace } from 'stores/workspaceStore';
import NotificationPanel from 'views/v2/applicant/notifications/components/NotificationPanel';
import ProgramQuestionSidebarStep from 'views/v2/programQuestions/containers/sidebarStep';

import PERMISSION from 'assets/permissions.json';
import { PageRoutes } from 'constant/pageRoute';
import useActiveApplication from 'hooks/useActiveApplication';
import useTranslate from 'hooks/useTranslate';
import { loadApplications } from 'redux/applications/applications-middleware';
import { logout } from 'redux/auth/auth-middleware';
import { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import { getDomainBranding } from 'utils/domainBranding';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

const SidebarLayout = () => {
    const dispatch: AppDispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const user = useSelector((state: RootState) => state.user.entity);
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const { permissionsList } = useSelector((state: RootState) => state.permissions);
    const applicationsUserId = selectedUserId || user?.id;

    const { translate } = useTranslate();
    const { selectedApplicationId, applications } = useActiveApplication();
    const { toggleLayoutMode } = useWorkspace();
    const branding = getDomainBranding();

    const [mobileOpen, setMobileOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    // const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    const hasEditProfileAccess = permissionsList.some(
        (permission) => permission?.name === PERMISSION?.EDIT
    );
    const hasViewAssessmentResult = permissionsList.some(
        (permission) => permission?.name === PERMISSION?.VIEW
    );

    useEffect(() => {
        if (applicationsUserId) {
            dispatch(loadApplications(applicationsUserId.toString()));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationsUserId]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const toggleSidebar = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    const handleUserMenuClick = () => {
        if (sidebarCollapsed) {
            setSidebarCollapsed(false);
            setUserMenuOpen(true);
            return;
        }
        setUserMenuOpen(!userMenuOpen);
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
        setUserMenuOpen(false);
        setMobileOpen(false);
    };

    const handleProfile = () => {
        navigate('/account/my-account');
        setUserMenuOpen(false);
        setMobileOpen(false);
    };

    // const handleHelp = () => {
    //   setUserMenuOpen(false);
    //   setMobileOpen(false);
    // };

    // const handleSwitchLayout = () => {
    //   toggleLayoutMode();
    //   setUserMenuOpen(false);
    //   setMobileOpen(false);
    // };

    const basePath = selectedApplicationId
        ? `/visa-process/${selectedApplicationId}`
        : '/visa-process';

    const hasApplication = applications && applications.length > 0;
    const selectedApplication = applications?.find(
        (app) => String(app.id) === String(selectedApplicationId)
    );

    const menuItems = [
        {
            label: translate('dashboard'),
            path: PageRoutes.dashboard,
            icon: <Dashboard fontSize="small" />,
            disabled: false,
        },
        ...(hasEditProfileAccess
            ? [
                  {
                      label: translate('myProfile'),
                      path: `/visa-process/assessment`,
                      icon: <Assessment fontSize="small" />,
                      disabled: false,
                  },
              ]
            : []),
        ...(hasApplication
            ? [
                  {
                      label: translate('consultationAgreement'),
                      path: `${basePath}/agreement`,
                      icon: <Gavel fontSize="small" />,
                      disabled: false,
                  },
                  {
                      label: translate('documents'),
                      path: `${basePath}/documents`,
                      icon: <Description fontSize="small" />,
                      disabled: false,
                  },
              ]
            : []),

        ...(hasViewAssessmentResult && hasApplication
            ? [
                  {
                      label: translate('reports'),
                      path: `${basePath}/results`,
                      icon: <Assessment fontSize="small" />,
                      disabled: false,
                  },
              ]
            : []),
        ...(hasApplication
            ? [
                  {
                      label: translate('chat'),
                      path: `${basePath}/chats`,
                      icon: <Chat fontSize="small" />,
                      disabled: false,
                  },
              ]
            : []),
    ];

    const sidebarContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Fixed Header - Logo with Toggle */}
            {/* <Box
        sx={{
          px: sidebarCollapsed ? 1.5 : 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          minHeight: 64,
          transition: 'all 0.3s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => navigate(PageRoutes.dashboard)}
        >
          <Box
            component="img"
            src={sidebarCollapsed ? '/logo_mini.png' : '/logo.png'}
            alt="Aliro Logo"
            sx={{
              height: 50,
              transition: 'all 0.3s',
            }}
          />
        </Box>

        {!sidebarCollapsed && (
          <IconButton
            onClick={toggleSidebar}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              width: 32,
              height: 32,
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
        )}

        {sidebarCollapsed && (
          <IconButton
            onClick={toggleSidebar}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              width: 32,
              height: 32,
              position: 'absolute',
              right: 8,
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        )}
      </Box> */}
            {/* Fixed Header - Logo with Toggle */}
            <Box
                sx={{
                    px: sidebarCollapsed ? 1.5 : 2,
                    py: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: sidebarCollapsed ? 'center' : 'space-between',
                    gap: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                    minHeight: 64,
                    transition: 'all 0.3s',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: sidebarCollapsed ? 'pointer' : 'pointer',
                        position: 'relative',
                        '&:hover .expand-icon': {
                            opacity: sidebarCollapsed ? 1 : 0,
                        },
                        '&:hover .logo-image': {
                            opacity: sidebarCollapsed ? 0 : 1,
                        },
                        justifyContent: 'center',
                    }}
                    onClick={
                        sidebarCollapsed ? toggleSidebar : () => navigate(PageRoutes.dashboard)
                    }
                >
                    {/* Logo - visible by default, hidden on hover when collapsed */}
                    {sidebarCollapsed ? (
                        <Box
                            component="img"
                            src={branding.logoMinimal}
                            alt={translate('logo')}
                            className="logo-image"
                            sx={{
                                width: '100%',
                                height: '50px',
                                borderRadius: 1,
                                opacity: 1,
                                transition: 'opacity 0.2s',
                            }}
                        />
                    ) : (
                        <Box
                            component="img"
                            src={branding.logoHeader}
                            alt={translate('logo')}
                            className="logo-image"
                            sx={{
                                width: '100%',
                                height: '50px',
                                borderRadius: 1,
                                opacity: 1,
                                transition: 'opacity 0.2s',
                            }}
                        />
                    )}

                    {/* Expand icon - hidden by default, visible on hover when collapsed */}
                    {sidebarCollapsed && (
                        <IconButton
                            className="expand-icon"
                            sx={{
                                position: 'absolute',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                width: 28,
                                height: 28,
                                padding: 0,
                                pointerEvents: 'none',
                            }}
                        >
                            <ChevronRight />
                        </IconButton>
                    )}
                </Box>

                {/* Toggle Button - only visible when expanded */}
                {!sidebarCollapsed && (
                    <IconButton
                        onClick={toggleSidebar}
                        sx={{
                            display: { xs: 'none', sm: 'flex' },
                            width: 32,
                            height: 32,
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <ChevronLeft fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {/* Scrollable Navigation */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <List sx={{ px: sidebarCollapsed ? 1 : 1.5, py: 2 }}>
                    {menuItems.map((item) => (
                        <ListItem key={item.path} disablePadding>
                            <Tooltip title={sidebarCollapsed ? item.label : ''} placement="right">
                                <ListItemButton
                                    onClick={() => !item.disabled && navigate(item.path)}
                                    disabled={item.disabled}
                                    sx={{
                                        borderRadius: sidebarCollapsed ? 1.5 : 1,
                                        mb: 0.5,
                                        justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                                        px: sidebarCollapsed ? 1.5 : 1.5,
                                        py: sidebarCollapsed ? 1 : 0.25,
                                        bgcolor:
                                            location.pathname === item.path
                                                ? 'rgba(102, 126, 234, 0.12)'
                                                : 'transparent',
                                        '&:hover': {
                                            bgcolor: item.disabled
                                                ? 'transparent'
                                                : location.pathname === item.path
                                                  ? 'rgba(102, 126, 234, 0.12)'
                                                  : 'rgba(0, 0, 0, 0.04)',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            minWidth: sidebarCollapsed ? 'auto' : 40,
                                            color:
                                                location.pathname === item.path
                                                    ? 'primary.main'
                                                    : item.disabled
                                                      ? 'rgba(0, 0, 0, 0.26)'
                                                      : 'text.secondary',
                                        },
                                        '& .MuiListItemText-primary': {
                                            color:
                                                location.pathname === item.path
                                                    ? 'primary.main'
                                                    : item.disabled
                                                      ? 'rgba(0, 0, 0, 0.26)'
                                                      : 'inherit',
                                            fontWeight: location.pathname === item.path ? 600 : 400,
                                        },
                                    }}
                                >
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    {!sidebarCollapsed && <ListItemText primary={item.label} />}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    ))}
                    <ProgramQuestionSidebarStep
                        collapsed={sidebarCollapsed}
                        userId={Number(applicationsUserId) || undefined}
                        country={selectedApplication?.country}
                        programName={selectedApplication?.program_name}
                    />
                </List>
            </Box>

            {!sidebarCollapsed && hasApplication ? <ApplicationSelector /> : null}
            {/* AI Assistant & Notifications - Fixed at Bottom */}
            <Box
                sx={{
                    px: sidebarCollapsed ? 1 : 1.5,
                    py: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <List sx={{ p: 0 }}>
                    {/* Notifications */}
                    <ListItem disablePadding>
                        <NotificationPanel variant="list-item" collapsed={sidebarCollapsed} />
                    </ListItem>

                    {/* Immigration Assistant */}
                    {/* <ListItem disablePadding>
            <Tooltip title={sidebarCollapsed ? 'Immigration Assistant' : ''} placement="right">
              <ListItemButton
                onClick={() => setAiAssistantOpen(true)}
                sx={{
                  borderRadius: sidebarCollapsed ? 1.5 : 1,
                  mb: 0.5,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  px: sidebarCollapsed ? 1.5 : 2,
                  py: sidebarCollapsed ? 1 : 0.25,
                  bgcolor: 'black',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'grey.900'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarCollapsed ? 'auto' : 40,
                    color: 'white'
                  }}
                >
                  <AutoAwesome fontSize="small" />
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary="Immigration Assistant" />}
              </ListItemButton>
            </Tooltip>
          </ListItem> */}
                </List>
            </Box>
            {/* Fixed Footer - User Menu */}
            {user && (
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                    }}
                >
                    <List sx={{ px: sidebarCollapsed ? 1 : 1.5, pt: 1, pb: 0 }}>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={handleUserMenuClick}
                                sx={{
                                    borderRadius: sidebarCollapsed ? 1.5 : 1,
                                    mb: 0.5,
                                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                                    px: 1.5,
                                    py: sidebarCollapsed ? 1 : 0.75,
                                    '&:hover': {
                                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                    '& .MuiListItemIcon-root': {
                                        minWidth: sidebarCollapsed ? 'auto' : 40,
                                    },
                                }}
                            >
                                <ListItemIcon>
                                    <ProfileAvatar size={32} showSkeleton={false} />
                                </ListItemIcon>
                                {!sidebarCollapsed && (
                                    <>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                                >
                                                    {`${user.firstname} ${user.lastname}`}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        display: 'block',
                                                    }}
                                                >
                                                    {user.email}
                                                </Typography>
                                            }
                                            sx={{ flex: 1, minWidth: 0 }}
                                        />
                                        <ExpandMore
                                            sx={{
                                                transform: userMenuOpen
                                                    ? 'rotate(180deg)'
                                                    : 'rotate(0deg)',
                                                transition: 'transform 0.3s',
                                                fontSize: 20,
                                                color: 'text.secondary',
                                            }}
                                        />
                                    </>
                                )}
                            </ListItemButton>
                        </ListItem>
                    </List>

                    <Collapse in={userMenuOpen && !sidebarCollapsed} timeout="auto">
                        <AccountSwitcher />
                        <List sx={{ px: 1.5, pt: 0, pb: 1 }}>
                            {[
                                {
                                    label: 'My Account',
                                    path: '/account/my-account',
                                    icon: <AccountCircle fontSize="small" />,
                                    onClick: handleProfile,
                                },
                                {
                                    label: 'Switch to Classic',
                                    path: null as string | null,
                                    icon: <ViewWeek fontSize="small" />,
                                    onClick: () => {
                                        toggleLayoutMode();
                                        setUserMenuOpen(false);
                                        setMobileOpen(false);
                                    },
                                },
                            ].map((item) => {
                                const isActive = item.path
                                    ? location.pathname === item.path
                                    : false;
                                return (
                                    <ListItem key={item.label} disablePadding>
                                        <ListItemButton
                                            onClick={item.onClick}
                                            sx={{
                                                borderRadius: 1,
                                                mb: 0.5,
                                                justifyContent: 'flex-start',
                                                px: 1.5,
                                                py: 0.25,
                                                bgcolor: isActive
                                                    ? 'rgba(102, 126, 234, 0.12)'
                                                    : 'transparent',
                                                '&:hover': {
                                                    bgcolor: isActive
                                                        ? 'rgba(102, 126, 234, 0.12)'
                                                        : 'rgba(0, 0, 0, 0.04)',
                                                },
                                                '& .MuiListItemIcon-root': {
                                                    minWidth: 40,
                                                    color: isActive
                                                        ? 'primary.main'
                                                        : 'text.secondary',
                                                },
                                                '& .MuiListItemText-primary': {
                                                    color: isActive ? 'primary.main' : 'inherit',
                                                    fontWeight: isActive ? 600 : 400,
                                                },
                                            }}
                                        >
                                            <ListItemIcon>{item.icon}</ListItemIcon>
                                            <ListItemText primary={item.label} />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}

                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={handleLogout}
                                    sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        justifyContent: 'flex-start',
                                        px: 1.5,
                                        py: 0.25,
                                        color: 'error.main',
                                        '&:hover': {
                                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            minWidth: 40,
                                            color: 'error.main',
                                        },
                                        '& .MuiListItemText-primary': {
                                            color: 'error.main',
                                            fontWeight: 400,
                                        },
                                    }}
                                >
                                    <ListItemIcon>
                                        <Logout fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Logout" />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Collapse>
                </Box>
            )}
        </Box>
    );

    return (
        <ThemeProvider theme={appThemeV2}>
            <Box
                sx={{
                    display: 'flex',
                    height: '100dvh',
                    minHeight: '100dvh',
                    bgcolor: '#fafafa',
                    overflow: 'hidden',
                }}
            >
                {/* Sidebar - Desktop */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
                        flexShrink: 0,
                        transition: 'width 0.3s',
                        '& .MuiDrawer-paper': {
                            width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            transition: 'width 0.3s',
                            overflowX: 'hidden',
                        },
                    }}
                >
                    {sidebarContent}
                </Drawer>

                {/* Sidebar - Mobile */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            width: 260,
                            boxSizing: 'border-box',
                        },
                    }}
                >
                    {sidebarContent}
                </Drawer>

                {/* Main Content */}
                <Box
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {/* Mobile Top AppBar */}
                    <AppBar
                        position="sticky"
                        elevation={1}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            bgcolor: 'white',
                            color: 'text.primary',
                        }}
                    >
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="menu"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 2 }}
                            >
                                <Menu />
                            </IconButton>
                            <Box
                                component="img"
                                src={branding.logoHeader}
                                alt={translate('logo')}
                                sx={{ height: 40, cursor: 'pointer' }}
                                onClick={() => navigate(PageRoutes.dashboard)}
                            />
                            <Box sx={{ flexGrow: 1 }} />
                            {/* <NotificationPanel variant="icon" /> */}
                            {/* <IconButton onClick={() => setAiAssistantOpen(true)} sx={{ ml: 1 }}>
                                <AutoAwesome />
                            </IconButton> */}
                        </Toolbar>
                    </AppBar>

                    <Box
                        component="main"
                        sx={{
                            flexGrow: 1,
                            minHeight: 0,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            // Pages that manage their own scroll (e.g. notifications)
                            // use flex:1 + overflow hidden; others scroll here.
                            overflow: 'auto',
                        }}
                    >
                        <Outlet />
                    </Box>
                </Box>

                {/* AI Assistant Dialog */}
                {/* <AIAssistantDialog open={aiAssistantOpen} onClose={() => setAiAssistantOpen(false)} /> */}
            </Box>
        </ThemeProvider>
    );
};

export default SidebarLayout;
